import type { MessageKey } from '@ecom/i18n';

/**
 * Mirrors apps/api/src/modules/identity/membership-repository.ts's
 * `MembershipWithPermissions` field-for-field — including the snake_case
 * fields the API itself returns verbatim (this repository, unlike most in
 * this codebase, exposes DB column names directly, with no camelCase
 * mapper — docs/resumée.txt entrée 61 confirms `email`/`role_key` were
 * added to this exact shape, not a new one).
 */
export type MembershipStatus = 'active' | 'suspended';

export interface StaffMember {
  readonly id: string;
  readonly tenant_id: string;
  readonly user_id: string;
  readonly role_id: string;
  readonly status: MembershipStatus;
  readonly email: string;
  readonly role_key: string;
  /** The account was created by this shop's owner: its password may be re-issued by them. */
  readonly managed: boolean;
  readonly permissions: readonly string[];
}

/**
 * Mirrors apps/api/src/modules/identity/rbac/rbac-matrix.ts's
 * `PERMISSION_KEYS` exactly — the six Merchant Staff flags DEV §5 names.
 * No endpoint exposes this list; it is stable, already-established
 * reference data, mirrored the same way every DTO field bound in this
 * project already is, not invented.
 */
export const PERMISSION_KEYS = [
  'catalog:write',
  'inventory:write',
  'orders:write',
  'customers:write',
  'marketing:write',
  'analytics:read',
] as const;
export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export const PERMISSION_LABEL_KEY: Record<PermissionKey, MessageKey> = {
  'catalog:write': 'merchant.staff.permission.catalog',
  'inventory:write': 'merchant.nav.inventory',
  'orders:write': 'merchant.orders.title',
  'customers:write': 'merchant.nav.customers',
  'marketing:write': 'merchant.nav.marketing',
  'analytics:read': 'merchant.staff.permission.analytics',
};
